import React, { useId } from "react";
import { useDeepCompareEffect } from "react-use";
import {
  convertPostIdToFaustId,
  getManifestationsPids,
  getMaterialTypes,
  getWorkPid
} from "../../core/utils/helpers/general";
import { WorkId } from "../../core/utils/types/ids";
import { AvailabilityLabels } from "../availability-label/availability-labels";
import ButtonFavourite from "../button-favourite/button-favourite";
import { Cover } from "../cover/cover";
import MaterialAvailabilityText from "./MaterialAvailabilityText/MaterialAvailabilityText";
import MaterialHeaderText from "./MaterialHeaderText";
import MaterialButtons from "./material-buttons/MaterialButtons";
import { resolveMaterialButtonsType } from "./material-buttons/resolveMaterialButtonsType";
import MaterialUnavailableNotice from "./MaterialUnavailableNotice/MaterialUnavailableNotice";
import MaterialPeriodical from "./periodical/MaterialPeriodical";
import { Manifestation, Work } from "../../core/utils/types/entities";
import { PeriodicalEdition } from "./periodical/helper";
import { useCollectPageStatistics } from "../../core/statistics/useStatistics";
import { statistics } from "../../core/statistics/statistics";
import {
  getManifestationLanguageCode,
  getWorkTitle
} from "../../apps/material/helper";
import { isPeriodical, shouldShowMaterialAvailabilityText } from "./helper";
import { first } from "lodash";
import { hasCorrectMaterialType } from "./material-buttons/helper";
import { ManifestationMaterialType } from "../../core/utils/types/material-type";
import { useAddFavorite } from "../button-favourite/useAddFavorite";

import { QuickLoanText } from "./QuickLoanText";

interface MaterialHeaderProps {
  wid: WorkId;
  work: Work;
  selectedManifestations: Manifestation[];
  setSelectedManifestations: (manifestations: Manifestation[]) => void;
  selectedPeriodical: PeriodicalEdition | null;
  selectPeriodicalHandler: (selectedPeriodical: PeriodicalEdition) => void;
  children: React.ReactNode;
  isGlobalMaterial: boolean;
  isAvailable: boolean | null;
}

const withQuickLoanInfo = document.querySelector("[data-material-quick-loan-info]")?.getAttribute("data-material-quick-loan-info") === "true";
const MaterialHeader: React.FC<MaterialHeaderProps> = ({
  work: {
    creators,
    manifestations: { all: manifestations, bestRepresentation },
    workId: wid
  },
  work,
  selectedManifestations,
  setSelectedManifestations,
  selectedPeriodical,
  selectPeriodicalHandler,
  children,
  isGlobalMaterial = false,
  isAvailable
}) => {
  const materialTitleId = useId();
  const addToListRequest = useAddFavorite({ app: "material" });
  const title = getWorkTitle(work);
  const pid = getWorkPid(work);
  const coverPids = getManifestationsPids(selectedManifestations);
  const { collectPageStatistics } = useCollectPageStatistics();
  // This is used to track whether the user is changing between material types or just clicking the same button over
  const manifestationMaterialTypes = getMaterialTypes(selectedManifestations);

  const languageCode = getManifestationLanguageCode(selectedManifestations);

  const isYearbook =
    hasCorrectMaterialType(
      ManifestationMaterialType.yearBook,
      manifestations
    ) ||
    hasCorrectMaterialType(
      ManifestationMaterialType.yearBookOnline,
      manifestations
    );

  useDeepCompareEffect(() => {
    collectPageStatistics({
      ...statistics.materialType,
      trackedData: manifestationMaterialTypes.join(", ")
    });
    collectPageStatistics({
      ...statistics.materialSource,
      trackedData: first(first(selectedManifestations)?.source) ?? "No source"
    });
    // We just want to track if the currently selected manifestation changes (which should be once - on initial render)
    // and when the currently selected manifestation's material type changes - on availability button click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestationMaterialTypes]);

  const buttonsType = resolveMaterialButtonsType(selectedManifestations);

  const materialUnavailableNotice = (
    <div className="material-header__button">
      <MaterialUnavailableNotice />
    </div>
  );

  return (
    <header className="border-bottom">
      <div className="material-header">
        <div className="material-header__cover">
          <Cover
            ids={coverPids}
            bestRepresentation={bestRepresentation}
            size="large"
            displaySize="xlarge"
            animate
            shadow="small"
          />
        </div>
        <div
          data-cy="material-header-content"
          className="material-header__content"
        >
          <ButtonFavourite
            title={String(title)}
            id={wid}
            addToListRequest={addToListRequest}
          />
          <MaterialHeaderText
            title={String(title)}
            creators={creators}
            languageCode={languageCode}
            materialTitleId={materialTitleId}
          />
          <div className="material-header__availability-label">
            {!isGlobalMaterial && (
              <AvailabilityLabels
                cursorPointer
                workId={wid}
                manifestations={manifestations}
                selectedManifestations={selectedManifestations}
                setSelectedManifestations={setSelectedManifestations}
              />
            )}
            { withQuickLoanInfo ? <QuickLoanText manifestations={ selectedManifestations } /> : null }
          </div>
          {/* Global works are not in the library's own catalogue, so they can
              neither be reserved nor loaned here. They only get the notice. */}
          {isGlobalMaterial && materialUnavailableNotice}
          {!isGlobalMaterial && (
            <>
              {isPeriodical(selectedManifestations) && (
                <MaterialPeriodical
                  faustId={convertPostIdToFaustId(pid)}
                  selectedPeriodical={selectedPeriodical}
                  selectPeriodicalHandler={selectPeriodicalHandler}
                  isYearbook={isYearbook}
                />
              )}
              {buttonsType ? (
                <div className="material-header__button">
                  <MaterialButtons
                    type={buttonsType}
                    manifestations={selectedManifestations}
                    workId={wid}
                    dataCy="material-header-buttons"
                    materialTitleId={materialTitleId}
                  />
                </div>
              ) : (
                materialUnavailableNotice
              )}
              {/* MaterialAvailabilityText is only shown for:
                - Online manifestations
                - physical manifestations
                - that are not periodical or articles
                - that are available in at least one local library branch
              */}
              {shouldShowMaterialAvailabilityText(selectedManifestations) &&
                isAvailable && (
                  <MaterialAvailabilityText
                    manifestations={selectedManifestations}
                  />
                )}
              {children}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default MaterialHeader;
